import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { api, formatBytes } from '../lib/api.js';

type Trash = Awaited<ReturnType<typeof api.adminTrash>>;

/**
 * Recovery and storage reclamation for deleted media.
 *
 * Deletes are soft everywhere in the app, which is what makes an accidental
 * bulk delete during a launch weekend survivable. The cost is that storage only
 * grows until something reclaims it — this is that something.
 */
export function TrashPanel(): ReactNode {
  const [trash, setTrash] = useState<Trash | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    api
      .adminTrash(search || undefined)
      .then(setTrash)
      .catch((err: Error) => setNotice({ kind: 'error', text: err.message }));
  }, [search]);

  useEffect(load, [load]);

  const run = async (
    action: () => Promise<string>,
  ): Promise<void> => {
    setBusy(true);
    setNotice(null);
    try {
      const message = await action();
      setSelected(new Set());
      load();
      setNotice({ kind: 'ok', text: message });
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  const restore = (): Promise<void> =>
    run(async () => {
      const result = await api.adminRestore([...selected]);
      let message = `Restored ${result.restored} file(s). They are back in Browse.`;
      if (result.processing > 0) {
        // These were deleted before they finished processing, so they come
        // back without a thumbnail until the worker catches up.
        message +=
          ` ${result.processing} still need processing and will get thumbnails ` +
          'and tags shortly.';
      }
      return message;
    });

  const purge = (): Promise<void> => {
    const count = selected.size;
    if (
      !confirm(
        `Permanently delete ${count} file(s)?\n\n` +
          'This removes them from the NAS. It cannot be undone.',
      )
    ) {
      return Promise.resolve();
    }

    return run(async () => {
      const result = await api.adminPurge({ assetIds: [...selected] });
      let message =
        `Purged ${result.assetsPurged} file(s), freeing ${formatBytes(result.bytesReclaimed)}.`;

      // Content-addressed storage means a deleted asset can share its bytes
      // with a live one. Saying so explains why the space freed is smaller
      // than the size of what was purged.
      if (result.blobsRetained > 0) {
        message +=
          ` ${result.blobsRetained} file(s) stayed on disk because other media ` +
          'shares those exact bytes.';
      }
      if (result.errors.length > 0) {
        message += ` ${result.errors.length} error(s) — check the server log.`;
      }
      return message;
    });
  };

  const previewCleanup = (days: number): Promise<void> =>
    run(async () => {
      const result = await api.adminPurge({ olderThanDays: days, dryRun: true });
      if (result.assetsPurged === 0) {
        return `Nothing has been in the trash longer than ${days} days.`;
      }
      return (
        `Purging everything deleted over ${days} days ago would remove ` +
        `${result.assetsPurged} file(s) and free ${formatBytes(result.bytesReclaimed)}. ` +
        'Nothing was changed — this was a preview.'
      );
    });

  const items = trash?.items ?? [];
  const allSelected = items.length > 0 && selected.size === items.length;

  return (
    <>
      {notice && (
        <div className={`notice ${notice.kind === 'error' ? 'error' : 'success'}`}>
          {notice.text}
        </div>
      )}

      <div className="panel">
        <h3>Deleted media</h3>
        <p style={{ fontSize: 13, color: 'var(--text-dim)', marginTop: 0 }}>
          Nothing here is gone yet. Files stay on the NAS until purged, so an accidental
          delete is recoverable — but storage keeps growing until someone reclaims it.
        </p>

        <dl className="kv">
          <dt>In trash</dt>
          <dd>{trash?.summary.count ?? 0} files</dd>
          <dt>Total size</dt>
          <dd>{trash ? formatBytes(trash.summary.bytes) : '—'}</dd>
          <dt>Reclaimable</dt>
          <dd title="Deleted copies of files that still exist elsewhere free no space when purged">
            {trash ? formatBytes(trash.summary.reclaimableBytes) : '—'}
            {trash && trash.summary.bytes !== trash.summary.reclaimableBytes && (
              <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                {' '}
                — the rest is shared with files that still exist
              </span>
            )}
          </dd>
          <dt>Oldest</dt>
          <dd>
            {trash?.summary.oldestDeletedAt
              ? new Date(trash.summary.oldestDeletedAt).toLocaleDateString()
              : '—'}
          </dd>
        </dl>

        <div className="flex" style={{ marginTop: 14, flexWrap: 'wrap' }}>
          <button className="btn" disabled={busy || selected.size === 0} onClick={() => void restore()}>
            Restore{selected.size > 0 ? ` ${selected.size}` : ''}
          </button>
          <button
            className="btn danger"
            disabled={busy || selected.size === 0}
            onClick={() => void purge()}
          >
            Delete{selected.size > 0 ? ` ${selected.size}` : ''} permanently
          </button>
          <div className="spacer" />
          <button className="btn sm" disabled={busy} onClick={() => void previewCleanup(30)}>
            Preview 30-day cleanup
          </button>
          <button className="btn sm" disabled={busy} onClick={() => void previewCleanup(90)}>
            Preview 90-day
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="flex" style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>Items</h3>
          <div className="spacer" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search filenames…"
            style={{ maxWidth: 220 }}
          />
          {items.length > 0 && (
            <button
              className="btn sm"
              onClick={() =>
                setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))
              }
            >
              {allSelected ? 'Clear' : 'Select all'}
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
            {search ? 'Nothing deleted matches that.' : 'Trash is empty.'}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th>File</th>
                <th style={{ width: 90 }}>Size</th>
                <th style={{ width: 150 }}>Deleted</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      style={{ width: 'auto' }}
                      checked={selected.has(item.id)}
                      onChange={() => {
                        const next = new Set(selected);
                        if (next.has(item.id)) next.delete(item.id);
                        else next.add(item.id);
                        setSelected(next);
                      }}
                      aria-label={`Select ${item.filename}`}
                    />
                  </td>
                  <td>
                    {item.filename}
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                      {item.folder_path ?? '/'}
                      {item.uploader && ` · ${item.uploader}`}
                      {item.shares_bytes_with_live && (
                        <span title="An identical file still exists, so purging this frees no space">
                          {' · shares bytes with a live file'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>{formatBytes(Number(item.size_bytes))}</td>
                  <td>{new Date(item.deleted_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
